import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { useEffect, useId, useState, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input } from '@/components/ui/Input'
import { api } from '@/lib/api'
import { cn } from '@/lib/cn'

/** GET /search (src/api/admin/search.py): typed hits, only of the types the role may read. */
export interface SearchHit {
  type: 'user' | 'payment' | 'job' | 'promo'
  id: string
  title: string
  subtitle: string
  /** where the hit opens in the admin («/users?user=4211») */
  path: string
  user_id?: number
}
export interface SearchResult {
  q: string
  items: SearchHit[]
  types?: string[]
}

const TYPE_LABEL: Record<SearchHit['type'], string> = {
  user: 'Пользователь',
  payment: 'Платёж',
  job: 'Генерация',
  promo: 'Промокод',
}

/** A query worth sending: two characters, or any number (a tg_id / a user id). */
export function searchable(q: string): boolean {
  const t = q.trim()
  return t.length >= 2 || /^\d+$/.test(t)
}

/** The value after the user stops typing for `ms`. */
function useDebounced(value: string, ms = 250): string {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

/** The list under the field; presentational (stories show every state). */
export function SearchResults({
  listId,
  q,
  result,
  loading,
  failed,
  active,
  onPick,
}: {
  listId: string
  q: string
  result?: SearchResult
  loading?: boolean
  failed?: boolean
  active: number
  onPick: (hit: SearchHit) => void
}) {
  let note: string | null = null
  if (failed) note = 'Поиск не ответил. Попробуйте ещё раз.'
  else if (!result || result.q !== q.trim()) note = loading ? 'Ищу…' : null
  else if (result.items.length === 0) note = 'Ничего не найдено: проверьте id, Telegram ID, @username или промокод.'
  return (
    <div className="absolute inset-x-0 top-full z-50 mt-1 overflow-hidden rounded-md border border-hairline bg-surface-1 shadow-e3">
      {note !== null ? (
        <p role="status" className={cn('px-3 py-2 text-caption', failed ? 'text-danger' : 'text-ink-3')}>
          {failed && <span aria-hidden>✕ </span>}
          {note}
        </p>
      ) : (
        <ul id={listId} role="listbox" aria-label="Найдено" className="max-h-80 overflow-y-auto py-1">
          {result?.items.map((h, i) => (
            <li
              key={`${h.type}:${h.id}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              className={cn(
                'cursor-pointer px-3 py-2 hover:bg-surface-3',
                i === active && 'bg-surface-3',
              )}
              onMouseDown={(e) => {
                e.preventDefault() // keep the focus: the field closes the list on blur
                onPick(h)
              }}
            >
              <span className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-body text-ink-1">{h.title}</span>
                <span className="shrink-0 text-caption text-ink-3">{TYPE_LABEL[h.type]}</span>
              </span>
              <span className="tnum block truncate text-caption text-ink-2">{h.subtitle}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * The search box of the admin header (audit 2026-10-22 §7 P1): a Telegram ID, a user id,
 * @username, a payment id (ours or the provider's), a generation id or a promo code. Arrows pick
 * a hit, Enter opens it, Escape closes the list. The server searches only what the role may
 * read, so a hit never opens a page that answers 403.
 */
export function GlobalSearch({ className }: { className?: string }) {
  const id = useId()
  const listId = `${id}-list`
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const debounced = useDebounced(q)
  const query = debounced.trim()
  const result = useQuery({
    queryKey: ['global-search', query],
    queryFn: () => api.get<SearchResult>(`/search?q=${encodeURIComponent(query)}`),
    enabled: open && searchable(query),
    staleTime: 15_000,
    meta: { silent: true },
  })
  const items = result.data?.q === q.trim() ? result.data.items : []

  function pick(hit: SearchHit) {
    setOpen(false)
    setQ('')
    navigate(hit.path)
  }
  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false)
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (items.length === 0) return
      setActive((a) => (a + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length)
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const hit = items[active] ?? items[0]
      if (hit) pick(hit)
    }
  }
  const showList = open && searchable(q)
  return (
    <div className={cn('relative w-full max-w-md', className)} role="search">
      <label htmlFor={id} className="sr-only">
        Поиск по админке
      </label>
      <Search aria-hidden size={16} strokeWidth={1.75} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-3" />
      <Input
        id={id}
        size="sm"
        type="search"
        autoComplete="off"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList && items.length > 0 ? `${listId}-${active}` : undefined}
        className="pl-9"
        placeholder="Поиск по админке"
        title="Найти по Telegram ID, @username, id платежа, генерации или промокоду"
        value={q}
        onChange={(e) => {
          setQ(e.target.value)
          setActive(0)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKey}
      />
      {showList && (
        <SearchResults
          listId={listId}
          q={q}
          result={result.data}
          loading={result.isFetching || debounced !== q}
          failed={result.isError}
          active={active}
          onPick={pick}
        />
      )}
    </div>
  )
}
